import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, ChevronDown, ChevronRight } from 'lucide-react';
import useMetaobjectDefinitions from '../hooks/useMetaobjectDefinitions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import SyncModal from '../components/SyncModal';

const STATUS_FILTERS = [
  { id: 'all',     label: 'All definitions' },
  { id: 'missing', label: 'Missing translations only' },
];

// Heuristic for metaobjects (no `distinct_count` per definition, only entry_count):
// translate-policy defs are "missing" iff nothing pushed yet OR has translated-not-pushed rows.
function isMissingTranslation(def) {
  if (def.translation_policy !== 'translate') return false;
  const pushed = def.pushed_count || 0;
  const translated = def.translated_count || 0;
  return pushed === 0 || translated > 0;
}

// Phase 22 — one table renderer reused for both the Translate and Skip
// sections. No more policy column; the section header conveys it.
function DefinitionsTable({ definitions, onRowClick }) {
  if (definitions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        Nothing here.
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Type</TableHead>
          <TableHead>Name</TableHead>
          <TableHead className="text-right">Entries</TableHead>
          <TableHead className="text-right">Fields</TableHead>
          <TableHead className="text-right">Pushed</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {definitions.map((def) => (
          <TableRow
            key={def.id}
            className="cursor-pointer"
            onClick={() => onRowClick(def.id)}
          >
            <TableCell className="font-mono text-xs">{def.type}</TableCell>
            <TableCell>{def.name}</TableCell>
            <TableCell className="text-right">{def.entry_count}</TableCell>
            <TableCell className="text-right text-muted-foreground">
              {Array.isArray(def.field_schema) ? def.field_schema.length : 0}
            </TableCell>
            <TableCell className="text-right text-muted-foreground">
              {def.pushed_count > 0 ? def.pushed_count : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function MetaobjectDefinitionsPage() {
  const navigate = useNavigate();
  const {
    definitions,
    filteredDefinitions,
    loading,
    error,
    search,
    setSearch,
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    fetchData,
  } = useMetaobjectDefinitions();

  const [statusFilter, setStatusFilter] = useState('all');
  // Skip section is collapsed by default (user instruction 2026-05-16).
  const [skipOpen, setSkipOpen] = useState(false);

  const translateDefs = useMemo(() => {
    let list = filteredDefinitions.filter((d) => d.translation_policy === 'translate');
    if (statusFilter === 'missing') list = list.filter(isMissingTranslation);
    return list;
  }, [filteredDefinitions, statusFilter]);

  const skipDefs = useMemo(
    () => filteredDefinitions.filter(
      (d) => d.translation_policy === 'skip' || !d.translation_policy,
    ),
    [filteredDefinitions],
  );

  const handleSyncClose = () => {
    setSyncModalOpen(false);
    fetchData();
  };

  if (!loading && !error && definitions.length === 0) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-foreground">Metaobjects</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-24">
          <Layers className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-base font-medium text-foreground mb-1">No metaobject data yet.</p>
          <p className="text-sm text-muted-foreground mb-4">Click Sync to scan your Shopify store.</p>
          <Button onClick={() => setSyncModalOpen(true)}>Sync from Shopify</Button>
        </div>
        <SyncModal
          open={syncModalOpen}
          onClose={handleSyncClose}
          endpoint="/api/transly/metaobjects/sync/"
          title="Syncing Metaobjects"
        />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-foreground">Metaobjects</h1>
        <Button onClick={() => setSyncModalOpen(true)}>Sync from Shopify</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {lastSynced
          ? `Last synced: ${new Date(lastSynced).toLocaleString()}`
          : 'Never synced'}
      </p>

      <Input
        type="text"
        placeholder="Filter by type or name..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-3"
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.id}
              variant={statusFilter === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatusFilter(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex flex-col items-center py-12">
          <p className="text-sm text-destructive mb-4">Failed to load definitions. Please try again.</p>
          <Button variant="outline" onClick={fetchData}>Retry</Button>
        </div>
      )}

      {loading && !error && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex gap-4 px-2 py-3">
              <div className="animate-pulse bg-muted h-4 rounded w-32" />
              <div className="animate-pulse bg-muted h-4 rounded w-40" />
              <div className="animate-pulse bg-muted h-4 rounded w-12" />
            </div>
          ))}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── Translate section (top, always open) ── */}
          <section className="mb-10">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-semibold tracking-wide text-foreground uppercase">
                To translate
              </h2>
              <span className="text-xs text-muted-foreground">
                {translateDefs.length} definition{translateDefs.length === 1 ? '' : 's'}
              </span>
            </div>
            {translateDefs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                {statusFilter === 'missing'
                  ? 'No definitions have missing translations. Nice.'
                  : 'No definitions set to translate.'}
              </p>
            ) : (
              <DefinitionsTable
                definitions={translateDefs}
                onRowClick={(id) => navigate(`/metaobjects/${id}`)}
              />
            )}
          </section>

          {/* ── Skip section (bottom, collapsed by default) ── */}
          <section>
            <button
              type="button"
              onClick={() => setSkipOpen((v) => !v)}
              className="w-full flex items-baseline justify-between mb-3 hover:opacity-90 cursor-pointer"
            >
              <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase flex items-center gap-1">
                {skipOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                Skipped / unset
              </h2>
              <span className="text-xs text-muted-foreground">
                {skipDefs.length} definition{skipDefs.length === 1 ? '' : 's'}
              </span>
            </button>
            {skipOpen && (
              <DefinitionsTable
                definitions={skipDefs}
                onRowClick={(id) => navigate(`/metaobjects/${id}`)}
              />
            )}
          </section>
        </>
      )}

      <SyncModal
        open={syncModalOpen}
        onClose={handleSyncClose}
        endpoint="/api/transly/metaobjects/sync/"
        title="Syncing Metaobjects"
      />
    </div>
  );
}
